import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { statusClass, statusLabel } from '../utils/statusBadge';
import { toast } from 'sonner';
import {
  DocumentTextIcon,
  PlusIcon,
  EyeIcon,
  TrashIcon,
  CheckIcon,
  PrinterIcon,
  MagnifyingGlassIcon,
  ChatBubbleLeftIcon,
  EnvelopeIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { Badge } from '@/components/ui/badge';
import { openPdf } from '../utils/pdfHelpers';
import { confirmDialog } from '../components/ConfirmDialog';
import { Tip } from '../components/ui/tip';

interface CreditNoteItem {
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
  code?: string;
}

interface CreditNote {
  id: string;
  creditNoteNumber: string;
  type: string;
  clientId: string;
  clientName: string;
  clientDocument: string;
  linkedInvoiceNumber: string;
  linkedInvoiceType?: string;
  total: number;
  subtotal?: number;
  taxRate?: number;
  taxAmount?: number;
  discount?: number;
  status: string;
  reason: string;
  issueDate: string;
  afipCae: string;
  afipCaeVto?: string;
  afipCbteNro: number;
  afipPtoVta: number;
  afipCbteTipo?: number;
  afipAuthorizedAt?: string;
  clientTaxCondition?: string;
  clientAddress?: string;
  items?: CreditNoteItem[];
  notes?: string;
  createdAt: string;
  createdBy?: string;
}

interface Client {
  id: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  email?: string;
  phone?: string;
  whatsapp?: string;
}

const REASONS: Record<string, string> = {
  DEVOLUCION: 'Devolución',
  ERROR_FACTURACION: 'Error de facturación',
  DESCUENTO: 'Descuento posterior',
  ANULACION: 'Anulación total',
  OTRO: 'Otro'
};

const CreditNotes: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlSearchQuery = (searchParams.get('q') || '').trim();
  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(urlSearchQuery);
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedCreditNote, setSelectedCreditNote] = useState<CreditNote | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const fetchClients = async () => {
    try {
      const { data } = await axios.get('/api/clients?limit=1000');
      setClients(data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchCreditNotes = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params: any = { page, limit: 20 };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;

      const { data } = await axios.get('/api/credit-notes', {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setCreditNotes(data.creditNotes || []);
      setPagination(data.pagination || { total: 0, page: 1, pages: 1 });
    } catch (error) {
      toast.error('Error cargando notas de crédito');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCreditNotes(); fetchClients(); }, []);

  useEffect(() => {
    const timer = setTimeout(() => fetchCreditNotes(), 300);
    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    setSearchTerm(urlSearchQuery);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [urlSearchQuery]);

  useEffect(() => {
    const handleGlobalShortcut = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      if (
        tagName === 'input' ||
        tagName === 'textarea' ||
        tagName === 'select' ||
        target?.isContentEditable
      ) {
        return;
      }

      if (event.key.toLowerCase() === 'n') {
        event.preventDefault();
        navigate('/notas-credito/nueva');
      }
    };

    window.addEventListener('keydown', handleGlobalShortcut);
    return () => window.removeEventListener('keydown', handleGlobalShortcut);
  }, [navigate]);

  const authorizeInAfip = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      toast.loading('Autorizando NC en AFIP...');
      await axios.post(`/api/credit-notes/${id}/authorize-afip`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.dismiss();
      toast.success('NC autorizada en AFIP exitosamente');
      fetchCreditNotes(pagination.page);
    } catch (error: any) {
      toast.dismiss();
      toast.error(error.response?.data?.error || 'Error autorizando NC en AFIP');
    }
  };

  const deleteCreditNote = async (id: string) => {
    if (!await confirmDialog('¿Eliminar esta nota de crédito?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/credit-notes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('NC eliminada');
      fetchCreditNotes(pagination.page);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error eliminando NC');
    }
  };

  const handleDownloadPdf = async (cn: CreditNote) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/credit-notes/${cn.id}/generate-pdf`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      openPdf({ blob: response.data, filename: `NC-${cn.creditNoteNumber}.pdf` });
    } catch {
      toast.error('Error generando PDF');
    }
  };

  const handleSendWhatsApp = (cn: CreditNote) => {
    const client = clients.find(c => c.id === cn.clientId);
    const phone = (client as any)?.phone || (client as any)?.whatsapp || '';
    const cleanPhone = phone.replace(/\D/g, '');
    const pv = String(cn.afipPtoVta || 2).padStart(4, '0');
    const nro = String(cn.afipCbteNro || cn.creditNoteNumber).padStart(8, '0');
    const typeLabel = cn.type === 'NCA' ? 'A' : 'B';
    const msg = encodeURIComponent(
      `Hola ${cn.clientName}, le enviamos la Nota de Crédito ${typeLabel} ${pv}-${nro} por un total de $${Number(cn.total).toLocaleString('es-AR', { minimumFractionDigits: 2 })}. CAE: ${cn.afipCae || 'Pendiente'}.`
    );
    const url = cleanPhone ? `https://wa.me/${cleanPhone}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(url, '_blank');
  };

  const handleSendEmail = (cn: CreditNote) => {
    const client = clients.find(c => c.id === cn.clientId);
    const email = client?.email || '';
    const pv = String(cn.afipPtoVta || 2).padStart(4, '0');
    const nro = String(cn.afipCbteNro || cn.creditNoteNumber).padStart(8, '0');
    const typeLabel = cn.type === 'NCA' ? 'A' : 'B';
    const subject = encodeURIComponent(`Nota de Crédito ${typeLabel} ${pv}-${nro}`);
    const body = encodeURIComponent(
      `Estimado/a ${cn.clientName},\n\nAdjuntamos la Nota de Crédito ${typeLabel} ${pv}-${nro} por un total de $${Number(cn.total).toLocaleString('es-AR', { minimumFractionDigits: 2 })}.\nCAE: ${cn.afipCae || 'Pendiente'}.\n\nSaludos.`
    );
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
  };

  const handleViewDetail = async (cn: CreditNote) => {
    try {
      setLoadingDetail(true);
      setShowViewModal(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`/api/credit-notes/${cn.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedCreditNote(data);
    } catch {
      toast.error('Error cargando detalle de la nota de crédito');
      setShowViewModal(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  const TAX_CONDITIONS: Record<string, string> = {
    RESPONSABLE_INSCRIPTO: 'Responsable Inscripto',
    MONOTRIBUTISTA: 'Monotributista',
    CONSUMIDOR_FINAL: 'Consumidor Final',
    EXENTO: 'Exento',
  };

  const fmtDate = (d: string) => {
    try {
      const date = new Date(d);
      return `${String(date.getDate()).padStart(2,'0')}/${String(date.getMonth()+1).padStart(2,'0')}/${date.getFullYear()}`;
    } catch { return d || '-'; }
  };

  const fmtMoney = (n: number) => Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const getStatusBadge = (status: string) => {
    const key = status === 'authorized' ? 'authorized' : 'draft';
    return <Badge className={statusClass('creditNote', key)}>{statusLabel('creditNote', key)}</Badge>;
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Notas de Crédito</h1>
        </div>
        <button
          onClick={() => navigate('/notas-credito/nueva')}
          className="inline-flex items-center justify-center whitespace-nowrap text-sm font-bold h-8 rounded-lg gap-1.5 px-3 bg-black dark:bg-white text-white dark:text-black shadow-xs hover:bg-black/90 dark:hover:bg-white/90"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Nueva NC
          <kbd className="ml-2 inline-flex items-center justify-center px-1 text-xs font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border leading-none">
            N
          </kbd>
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar por número o cliente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-800 rounded-xl bg-background text-sm"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-200 dark:border-gray-800 rounded-xl bg-background text-sm"
        >
          <option value="">Todos los estados</option>
          <option value="draft">Borrador</option>
          <option value="authorized">Autorizada</option>
        </select>
      </div>

      <div className="bg-background rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin h-8 w-8 border-2 border-gray-900 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-muted-foreground">Cargando...</p>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-muted/50 border-b">
                  <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Número</TableHead>
                  <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Cliente</TableHead>
                  <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Factura Asoc.</TableHead>
                  <TableHead className="cq-hide-narrow text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Motivo</TableHead>
                  <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Total</TableHead>
                  <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Fecha</TableHead>
                  <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
                  <TableHead className="text-foreground h-8 px-2 text-right align-middle font-medium whitespace-nowrap"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {creditNotes.map((cn) => (
                  <TableRow key={cn.id} className="border-b hover:bg-muted/50">
                    <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left font-medium">{cn.creditNoteNumber}</TableCell>
                    <TableCell className="cq-clamp-narrow py-1 px-2 align-middle whitespace-nowrap text-left truncate max-w-[220px]">{cn.clientName}</TableCell>
                    <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left text-muted-foreground">{cn.linkedInvoiceNumber || '-'}</TableCell>
                    <TableCell className="cq-hide-narrow py-1 px-2 align-middle whitespace-nowrap text-left">{REASONS[cn.reason] || cn.reason || '-'}</TableCell>
                    <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left font-medium">$ {fmtMoney(cn.total)}</TableCell>
                    <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">{fmtDate(cn.issueDate || cn.createdAt)}</TableCell>
                    <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">{getStatusBadge(cn.status)}</TableCell>
                    <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end">
                        <ButtonGroup>
                          <Tip label="Ver detalles">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleViewDetail(cn)}
                              className="size-8"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </Button>
                          </Tip>
                          <Tip label="Ver PDF">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleDownloadPdf(cn)}
                              className="size-8"
                            >
                              <PrinterIcon className="h-4 w-4" />
                            </Button>
                          </Tip>
                          {cn.status === 'authorized' && (
                            <>
                              <Tip label="Enviar por WhatsApp">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => handleSendWhatsApp(cn)}
                                  className="size-8"
                                >
                                  <ChatBubbleLeftIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                              <Tip label="Enviar por Email">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => handleSendEmail(cn)}
                                  className="size-8"
                                >
                                  <EnvelopeIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                            </>
                          )}
                          {cn.status === 'draft' && (
                            <>
                              <Tip label="Autorizar en AFIP">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => authorizeInAfip(cn.id)}
                                  className="size-8"
                                >
                                  <CheckIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                              <Tip label="Eliminar">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => deleteCreditNote(cn.id)}
                                  className="size-8"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                            </>
                          )}
                        </ButtonGroup>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {creditNotes.length === 0 && (
              <div className="text-center py-12">
                <DocumentTextIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No hay notas de crédito</p>
              </div>
            )}

            {pagination.pages > 1 && (
              <div className="flex items-center justify-center gap-2 py-4 border-t">
                <Button variant="outline" size="sm" disabled={pagination.page <= 1} onClick={() => fetchCreditNotes(pagination.page - 1)}>Anterior</Button>
                <span className="text-sm text-muted-foreground">Página {pagination.page} de {pagination.pages}</span>
                <Button variant="outline" size="sm" disabled={pagination.page >= pagination.pages} onClick={() => fetchCreditNotes(pagination.page + 1)}>Siguiente</Button>
              </div>
            )}
          </>
        )}
      </div>
      {/* Modal de detalle */}
      {showViewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowViewModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto mx-4" onClick={(e) => e.stopPropagation()}>
            {loadingDetail ? (
              <div className="text-center py-12">
                <div className="animate-spin h-8 w-8 border-2 border-gray-900 dark:border-white border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-muted-foreground">Cargando detalle...</p>
              </div>
            ) : selectedCreditNote ? (
              <>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Detalle de Nota de Crédito
                  </h3>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Info principal */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-900 dark:text-white">Información General</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Número:</span>
                          <span className="font-medium">{selectedCreditNote.creditNoteNumber}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Tipo:</span>
                          <span className="font-medium">{selectedCreditNote.type === 'NCA' ? 'Nota de Crédito A' : 'Nota de Crédito B'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Fecha emisión:</span>
                          <span>{fmtDate(selectedCreditNote.issueDate || selectedCreditNote.createdAt)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Estado:</span>
                          {getStatusBadge(selectedCreditNote.status)}
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Motivo:</span>
                          <span>{REASONS[selectedCreditNote.reason] || selectedCreditNote.reason || '-'}</span>
                        </div>
                        {selectedCreditNote.linkedInvoiceNumber && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Factura asociada:</span>
                            <span className="font-medium">{selectedCreditNote.linkedInvoiceNumber}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-900 dark:text-white">Datos del Cliente</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Nombre:</span>
                          <span className="font-medium">{selectedCreditNote.clientName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">CUIT/DNI:</span>
                          <span>{selectedCreditNote.clientDocument || '-'}</span>
                        </div>
                        {selectedCreditNote.clientTaxCondition && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Cond. fiscal:</span>
                            <span>{TAX_CONDITIONS[selectedCreditNote.clientTaxCondition] || selectedCreditNote.clientTaxCondition}</span>
                          </div>
                        )}
                        {selectedCreditNote.clientAddress && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Dirección:</span>
                            <span>{selectedCreditNote.clientAddress}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Info AFIP */}
                  {selectedCreditNote.afipCae && (
                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                      <h4 className="font-medium text-green-800 dark:text-green-300 mb-2">Datos ARCA/AFIP</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-green-700 dark:text-green-400">CAE:</span>
                          <span className="font-mono font-medium text-green-900 dark:text-green-200">{selectedCreditNote.afipCae}</span>
                        </div>
                        {selectedCreditNote.afipCaeVto && (
                          <div className="flex justify-between">
                            <span className="text-green-700 dark:text-green-400">Vto. CAE:</span>
                            <span className="text-green-900 dark:text-green-200">{fmtDate(selectedCreditNote.afipCaeVto)}</span>
                          </div>
                        )}
                        {selectedCreditNote.afipPtoVta && (
                          <div className="flex justify-between">
                            <span className="text-green-700 dark:text-green-400">Pto. Venta:</span>
                            <span className="text-green-900 dark:text-green-200">{String(selectedCreditNote.afipPtoVta).padStart(4, '0')}</span>
                          </div>
                        )}
                        {selectedCreditNote.afipCbteNro && (
                          <div className="flex justify-between">
                            <span className="text-green-700 dark:text-green-400">Nro. Comprobante:</span>
                            <span className="text-green-900 dark:text-green-200">{String(selectedCreditNote.afipCbteNro).padStart(8, '0')}</span>
                          </div>
                        )}
                        {selectedCreditNote.afipAuthorizedAt && (
                          <div className="flex justify-between">
                            <span className="text-green-700 dark:text-green-400">Fecha autorización:</span>
                            <span className="text-green-900 dark:text-green-200">{fmtDate(selectedCreditNote.afipAuthorizedAt)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Items */}
                  {selectedCreditNote.items && selectedCreditNote.items.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-3">Items</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                          <thead>
                            <tr className="text-left text-xs font-medium text-muted-foreground uppercase">
                              <th className="pb-2 pr-4">Descripción</th>
                              <th className="pb-2 pr-4 text-right">Cant.</th>
                              <th className="pb-2 pr-4 text-right">Precio Unit.</th>
                              <th className="pb-2 text-right">Total</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {selectedCreditNote.items.map((item, idx) => (
                              <tr key={idx} className="text-sm">
                                <td className="py-2 pr-4">{item.description}</td>
                                <td className="py-2 pr-4 text-right">{item.quantity}</td>
                                <td className="py-2 pr-4 text-right">$ {fmtMoney(item.unitPrice)}</td>
                                <td className="py-2 text-right font-medium">$ {fmtMoney(item.total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Totales */}
                  <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                    <div className="flex flex-col items-end space-y-1 text-sm">
                      {selectedCreditNote.subtotal !== undefined && (
                        <div className="flex justify-between w-64">
                          <span className="text-muted-foreground">Subtotal:</span>
                          <span>$ {fmtMoney(selectedCreditNote.subtotal)}</span>
                        </div>
                      )}
                      {(selectedCreditNote.discount ?? 0) > 0 && (
                        <div className="flex justify-between w-64">
                          <span className="text-muted-foreground">Descuento:</span>
                          <span className="text-red-600">-$ {fmtMoney(selectedCreditNote.discount!)}</span>
                        </div>
                      )}
                      {selectedCreditNote.taxAmount !== undefined && (
                        <div className="flex justify-between w-64">
                          <span className="text-muted-foreground">IVA ({selectedCreditNote.taxRate || 21}%):</span>
                          <span>$ {fmtMoney(selectedCreditNote.taxAmount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between w-64 font-bold text-base pt-1 border-t border-gray-200 dark:border-gray-600">
                        <span>Total:</span>
                        <span>$ {fmtMoney(selectedCreditNote.total)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Observaciones */}
                  {selectedCreditNote.notes && (
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">Observaciones</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 p-3 rounded">{selectedCreditNote.notes}</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600 flex justify-end">
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-sm"
                  >
                    Cerrar
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditNotes;
